/* eslint-disable no-console */

/**
 * Lightweight singleton that lets any part of the app ask the global
 * E2EERecoveryPasswordRequest dialog to (re-)prompt for an account's E2EE
 * passcode. Mirrors the callback pattern used by E2EEStatus.
 *
 * When a decrypt fails because the viewer has no usable local private key,
 * the UI calls `request(accountId)`. The dialog subscribes via `onRequest`
 * and reopens itself (clearing any one-time "Close" bypass) so the user can
 * recover in place instead of being stuck on a dead error.
 *
 * Two kinds of failures call in, and they want different behavior:
 *
 *   - Background bulk loads (the thumbnail grid, event lists) fail dozens at a
 *     time. These call `request(accountId)` and are DE-DUPED per account: only
 *     the first fires the dialog; the rest are ignored until `resolve()`. This
 *     prevents a single page load from firing the prompt many times.
 *
 *   - Explicit user actions (opening a full-size image) call
 *     `request(accountId, { force: true })`. A force request always re-opens
 *     the dialog, even if the user previously dismissed it with "Close" — if
 *     you're actively trying to view encrypted content, you should get the
 *     prompt. Force still respects the bulk de-dupe for the rest of the wave.
 */
class E2EEPasscodePrompt {
  constructor() {
    this.onRequest = null
    this.pending = new Set()
    this.unlockListeners = new Set()
  }

  // Components that failed to decrypt subscribe here so they can retry once the
  // passcode is entered. Returns an unsubscribe function.
  onUnlock(listener) {
    this.unlockListeners.add(listener)
    return () => this.unlockListeners.delete(listener)
  }

  // Called after a passcode is successfully entered for an account. Clears the
  // de-dupe state and tells subscribed components to re-attempt decryption.
  notifyUnlocked(accountId) {
    this.resolve(accountId)
    for (const listener of this.unlockListeners) {
      try {
        listener(accountId)
      } catch (e) {
        console.log(e)
      }
    }
  }

  request(accountId, opts = {}) {
    if (!accountId) {
      return
    }
    if (!opts.force && this.pending.has(accountId)) {
      // Background bulk load already prompted for this account; ignore.
      return
    }
    this.pending.add(accountId)
    console.log('E2EEPasscodePrompt: passcode requested for ' + accountId + (opts.force ? ' (forced)' : ''))
    if (this.onRequest) {
      try {
        this.onRequest(accountId)
      } catch (e) {
        console.log(e)
      }
    }
  }

  // Called once the prompt for an account is no longer active (passcode
  // entered) so a future genuine failure can prompt again.
  resolve(accountId) {
    if (accountId) {
      this.pending.delete(accountId)
    } else {
      this.pending.clear()
    }
  }
}

export default (new E2EEPasscodePrompt())
